import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ShipTo, ShipToSearchParams } from '../../services/shipToApi';

export interface ShipToState {
  items: ShipTo[];
  selectedItem: ShipTo | null;
  loading: boolean;
  error: string | null;
  validationErrors: string[];
  pagination: { page: number; pageSize: number; total: number };
  searchParams: ShipToSearchParams;
  selectedIds: number[];
  bulkLoading: boolean;
  successMessage: string;
}

const initialState: ShipToState = {
  items: [], selectedItem: null, loading: false, error: null, validationErrors: [],
  pagination: { page: 1, pageSize: 10, total: 0 },
  searchParams: { page: 1, pageSize: 10, search: '', active: undefined, sortBy: 'AttName', sortOrder: 'asc' },
  selectedIds: [], bulkLoading: false, successMessage: '',
};

const shipToSlice = createSlice({
  name: 'shipTo', initialState,
  reducers: {
    setLoading: (s, a: PayloadAction<boolean>) => { s.loading = a.payload; },
    setError: (s, a: PayloadAction<string | null>) => { s.error = a.payload; },
    setItems: (s, a: PayloadAction<ShipTo[]>) => { s.items = a.payload; },
    setSelectedItem: (s, a: PayloadAction<ShipTo | null>) => { s.selectedItem = a.payload; },
    addItem: (s, a: PayloadAction<ShipTo>) => { s.items.unshift(a.payload); },
    updateItem: (s, a: PayloadAction<ShipTo>) => { const i = s.items.findIndex(x => x.Id === a.payload.Id); if (i !== -1) s.items[i] = a.payload; if (s.selectedItem?.Id === a.payload.Id) s.selectedItem = a.payload; },
    removeItem: (s, a: PayloadAction<number>) => { s.items = s.items.filter(x => x.Id !== a.payload); if (s.selectedItem?.Id === a.payload) s.selectedItem = null; },
    setValidationErrors: (s, a: PayloadAction<string[]>) => { s.validationErrors = a.payload; },
    clearValidationErrors: (s) => { s.validationErrors = []; },
    setPagination: (s, a: PayloadAction<Partial<ShipToState['pagination']>>) => { s.pagination = { ...s.pagination, ...a.payload } as any; },
    setSearchParams: (s, a: PayloadAction<Partial<ShipToSearchParams>>) => { s.searchParams = { ...s.searchParams, ...a.payload } as any; },
    setSelectedIds: (s, a: PayloadAction<number[]>) => { s.selectedIds = a.payload; },
    addSelectedId: (s, a: PayloadAction<number>) => { if (!s.selectedIds.includes(a.payload)) s.selectedIds.push(a.payload); },
    removeSelectedId: (s, a: PayloadAction<number>) => { s.selectedIds = s.selectedIds.filter(id => id !== a.payload); },
    clearSelectedIds: (s) => { s.selectedIds = []; },
    setBulkLoading: (s, a: PayloadAction<boolean>) => { s.bulkLoading = a.payload; },
    setSuccessMessage: (s, a: PayloadAction<string>) => { s.successMessage = a.payload; },
    clearSuccessMessage: (s) => { s.successMessage = ''; },
    resetShipToState: () => initialState,
  }
});

export const {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, clearValidationErrors,
  setPagination, setSearchParams,
  setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds,
  setBulkLoading, setSuccessMessage, clearSuccessMessage, resetShipToState,
} = shipToSlice.actions;

export const FETCH_SHIPTO = 'shipTo/fetch';
export const FETCH_SHIPTO_BY_ID = 'shipTo/fetchById';
export const CREATE_SHIPTO = 'shipTo/create';
export const UPDATE_SHIPTO = 'shipTo/update';
export const DELETE_SHIPTO = 'shipTo/delete';
export const SEARCH_SHIPTO = 'shipTo/search';
export const BULK_ACTIVATE_SHIPTO = 'shipTo/bulkActivate';
export const BULK_DEACTIVATE_SHIPTO = 'shipTo/bulkDeactivate';
export const BULK_DELETE_SHIPTO = 'shipTo/bulkDelete';
export const FETCH_SHIPTO_STATS = 'shipTo/fetchStats';
export const EXPORT_SHIPTO_CSV = 'shipTo/exportCsv';

export const fetchShipTo = (p?: ShipToSearchParams) => ({ type: FETCH_SHIPTO, payload: p });
export const fetchShipToById = (id: number) => ({ type: FETCH_SHIPTO_BY_ID, payload: id });
export const createShipTo = (item: Omit<ShipTo, 'Id'>) => ({ type: CREATE_SHIPTO, payload: item });
export const updateShipTo = (id: number, item: Partial<ShipTo>) => ({ type: UPDATE_SHIPTO, payload: { id, item } });
export const deleteShipTo = (id: number) => ({ type: DELETE_SHIPTO, payload: id });
export const searchShipTo = (p: ShipToSearchParams) => ({ type: SEARCH_SHIPTO, payload: p });
export const bulkActivateShipTo = (ids: number[]) => ({ type: BULK_ACTIVATE_SHIPTO, payload: ids });
export const bulkDeactivateShipTo = (ids: number[]) => ({ type: BULK_DEACTIVATE_SHIPTO, payload: ids });
export const bulkDeleteShipTo = (ids: number[]) => ({ type: BULK_DELETE_SHIPTO, payload: ids });
export const fetchShipToStats = () => ({ type: FETCH_SHIPTO_STATS });
export const exportShipToCsv = (p?: ShipToSearchParams) => ({ type: EXPORT_SHIPTO_CSV, payload: p });

export default shipToSlice.reducer; 